/**
 * Documentation Tab Component for Admin Panel
 * Complete documentation of Customer App and Admin App functionality
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Book, Users, Palette, CreditCard, Settings, MessageSquare, 
  Shield, Bug, DollarSign, FileText, Layers, Share2, Download,
  UserPlus, Eye, Edit3, ChevronDown, ChevronRight, Sparkles,
  Globe, Mail, Building, Zap, Layout, Image, Code
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 rounded-lg mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-zinc-50 hover:bg-zinc-100 rounded-t-lg"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-indigo-600" />
          <span className="font-semibold text-zinc-900">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </button>
      {isOpen && <div className="p-4 bg-white rounded-b-lg">{children}</div>}
    </div>
  );
};

const Feature = ({ title, description, credits, endpoint }) => (
  <div className="border-l-4 border-indigo-200 pl-4 py-2 mb-3">
    <h4 className="font-medium text-zinc-900">{title}</h4>
    <p className="text-sm text-zinc-600 mt-1">{description}</p>
    {credits && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded mt-1 inline-block">Credits: {credits}</span>}
    {endpoint && <code className="text-xs bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded mt-1 ml-2">{endpoint}</code>}
  </div>
);

const Role = ({ name, color, permissions }) => (
  <div className={`border rounded-lg p-3 mb-2 ${color}`}>
    <h4 className="font-semibold mb-2">{name}</h4>
    <ul className="text-sm space-y-1">
      {permissions.map((p, i) => <li key={i}>• {p}</li>)}
    </ul>
  </div>
);

export const DocumentationTab = () => {
  const [activeSection, setActiveSection] = useState('customer');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Book className="w-8 h-8" />
          <h1 className="text-2xl font-bold">AI Design Studio Documentation</h1>
        </div>
        <p className="text-indigo-100">Complete reference guide for Customer App and Admin App functionality</p>
        <p className="text-xs text-indigo-200 mt-2">Last Updated: January 2026 | Version: Beta</p>
      </div>

      {/* Navigation */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'customer', label: 'Customer App', icon: Users },
          { id: 'admin', label: 'Admin App', icon: Shield },
          { id: 'api', label: 'API Reference', icon: Code },
          { id: 'credits', label: 'Credits System', icon: CreditCard }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
              activeSection === tab.id 
                ? 'bg-indigo-600 text-white' 
                : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Customer App Documentation */}
      {activeSection === 'customer' && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-600" />
            Customer App Documentation
          </h2>

          <Section title="1. Landing Page" icon={Globe} defaultOpen={true}>
            <Feature 
              title="Public Landing Page"
              description="Marketing page showcasing the product. Contains hero section, features, how it works, pricing info, and contact form."
              endpoint="/"
            />
            <Feature 
              title="Navigation Menu"
              description="Header menu with links to: Features, How It Works, Pricing, Contact. Sticky header with smooth scroll navigation."
            />
            <Feature 
              title="Contact Form"
              description="Allows visitors to submit inquiries. Fields: First Name, Last Name, Email, Company (optional), Inquiry Type, Message."
              endpoint="POST /api/utilities/contact"
            />
            <Feature 
              title="Get Started Flow"
              description="Lead capture page before sign-in. Collects: Name, Email, Business Email (optional), Company, Website URL."
              endpoint="/get-started"
            />
          </Section>

          <Section title="2. Authentication" icon={Shield}>
            <Feature 
              title="Google OAuth Sign-In"
              description="Users authenticate via Google OAuth 2.0. New users automatically get a workspace created with 50 free credits."
              endpoint="GET /api/auth/google/login"
            />
            <Feature 
              title="Session Management"
              description="Sessions stored in MongoDB with 7-day expiry. Session token stored in HTTP-only cookie."
            />
            <Feature 
              title="Account Deletion"
              description="Users can delete their account from Settings. Removes all user data, projects, and workspace memberships."
              endpoint="DELETE /api/auth/account"
            />
          </Section>

          <Section title="3. Dashboard" icon={Layout}>
            <Feature 
              title="Projects Overview"
              description="Shows all user's projects with status badges. Cards display: Project name, business name, status, created date, page count."
              endpoint="GET /api/projects"
            />
            <Feature 
              title="Project Status Badges"
              description="draft (gray), generating (amber pulse), ready (green), frozen (blue), revision_requested (purple)"
            />
            <Feature 
              title="Create New Project"
              description="Button redirects to Theme Selection page to start new project."
            />
            <Feature 
              title="Credit Balance Display"
              description="Shows current workspace credit balance in header."
            />
          </Section>

          <Section title="4. Theme Selection" icon={Palette}>
            <Feature 
              title="WordPress Theme Browser"
              description="Browse and search WordPress.org themes. Themes are cached in MongoDB for fast loading (~0.2s vs 19s uncached)."
              endpoint="GET /api/themes/wordpress"
            />
            <Feature 
              title="Theme Categories"
              description="Filter by: All, Business, Portfolio, Blog, E-commerce, Creative"
            />
            <Feature 
              title="Theme Preview"
              description="Click theme card to see larger preview with color scheme extraction."
            />
            <Feature 
              title="WordPress.org Theme Selection"
              description="AI recommends themes from WordPress.org when local library themes don't match well. Customer can select these for immediate design generation."
            />
            <Feature 
              title="Theme Installation Request (Auto)"
              description="When customer selects a WordPress.org theme and clicks 'Generate Design', an installation request is automatically created for admin. Design generates immediately using WordPress.org theme data while request is pending."
              endpoint="POST /api/themes/request-installation"
            />
            <Feature 
              title="Theme Selection"
              description="Selecting a theme proceeds to AI Briefing page."
            />
          </Section>

          <Section title="5. AI Briefing" icon={Sparkles}>
            <Feature 
              title="Business Information Form"
              description="Collects: Business Name, Industry, Target Audience, Business Description, Key Services/Products"
            />
            <Feature 
              title="Competitor/Reference URLs"
              description="User can add up to 5 reference website URLs. AI analyzes these for design inspiration."
              endpoint="POST /api/ai/analyze-urls"
            />
            <Feature 
              title="Page Selection"
              description="Choose which pages to generate: Homepage, About, Services, Contact, Portfolio, Blog, Pricing, FAQ, Team, Testimonials"
            />
            <Feature 
              title="Design Preferences"
              description="Color preferences, style preferences (Modern, Classic, Bold, Minimal), tone of voice"
            />
            <Feature 
              title="AI Design Generation"
              description="Generates complete multi-page website design using Gemini 3 Pro. Creates Elementor-compatible JSON structure."
              credits="20 credits"
              endpoint="POST /api/ai/generate-design"
            />
          </Section>

          <Section title="6. Design Preview" icon={Eye}>
            <Feature 
              title="Visual Page Preview"
              description="Full responsive preview of generated design. Toggle between Desktop/Tablet/Mobile views."
            />
            <Feature 
              title="Page Navigation"
              description="Sidebar with all generated pages. Click to switch between pages."
            />
            <Feature 
              title="Section Renderer"
              description="Renders design sections: Hero, Features, About, Services, Stats, Testimonials, Team, CTA, Contact, FAQ, Pricing, Gallery"
            />
            <Feature 
              title="Share Design"
              description="Generate public shareable link for the design. Link can be revoked."
              endpoint="POST /api/designs/{id}/share"
            />
            <Feature 
              title="Request Revision"
              description="Submit revision request with specific feedback. Creates new design version."
              credits="5 credits"
              endpoint="POST /api/ai/revise"
            />
            <Feature 
              title="Add New Page"
              description="Generate additional pages for existing design."
              credits="5 credits"
              endpoint="POST /api/ai/generate-page"
            />
            <Feature 
              title="Export Options"
              description="Export designs as: PNG Image, JPG Image, HTML/CSS, Elementor JSON, Figma JSON"
              credits="2 credits per export"
              endpoint="POST /api/designs/{id}/export"
            />
            <Feature 
              title="Freeze Design"
              description="Lock design and proceed to order. Prevents further edits."
              endpoint="POST /api/projects/{id}/freeze"
            />
          </Section>

          <Section title="7. Order & Payment" icon={CreditCard}>
            <Feature 
              title="Plan Selection"
              description="Choose from: Design Only, Design + Development, Custom Package"
            />
            <Feature 
              title="Razorpay Integration"
              description="Secure payment processing via Razorpay. Supports UPI, Cards, Net Banking."
              endpoint="POST /api/payments/create-order"
            />
            <Feature 
              title="Payment Verification"
              description="Automatic verification of payment signature. Updates project status on success."
              endpoint="POST /api/payments/verify"
            />
            <Feature 
              title="Payment History"
              description="View all past payments and invoices."
              endpoint="GET /api/payments/history"
            />
          </Section>

          <Section title="8. Credits System" icon={DollarSign}>
            <Feature 
              title="Credit Packages"
              description="Purchase credits: 100 credits ($6/₹499), 300 credits ($16/₹1299 - 13% off), 500 credits ($24/₹1999 - 20% off)"
            />
            <Feature 
              title="Credit Actions"
              description="AI Generation: 20, Revision: 5, Add Page: 5, Export: 2 credits"
            />
            <Feature 
              title="Transaction History"
              description="View all credit transactions with descriptions and timestamps."
              endpoint="GET /api/credits/history/{workspace_id}"
            />
          </Section>

          <Section title="9. Team & Workspace" icon={UserPlus}>
            <Feature 
              title="Workspace Model"
              description="Each user has a workspace. Credits are shared across workspace members."
            />
            <Feature 
              title="Invite Team Members"
              description="Generate invite links with role assignment. Links expire after 7 days."
              endpoint="POST /api/workspaces/invite"
            />
            <Feature 
              title="Team Roles"
              description="Owner (full access), Editor (create/edit), Viewer (read-only), Design Reviewer, QA Tester"
            />
            <Feature 
              title="Manage Members"
              description="View team members, remove members, change roles."
              endpoint="GET /api/workspaces/members"
            />
          </Section>

          <Section title="10. Support & Feedback" icon={MessageSquare}>
            <Feature 
              title="Support Tickets"
              description="Create support tickets with priority levels. Attach screenshots."
              endpoint="POST /api/support/tickets"
            />
            <Feature 
              title="Feature Requests"
              description="Submit and vote on feature requests. AI-powered prioritization for admins."
              endpoint="POST /api/feature-requests"
            />
            <Feature 
              title="Bug Reports"
              description="Report bugs with reproduction steps and screenshots."
            />
          </Section>

          <Section title="11. Settings" icon={Settings}>
            <Feature 
              title="Profile Settings"
              description="View account info, email, workspace ID."
            />
            <Feature 
              title="Delete Account"
              description="Permanently delete account and all associated data."
            />
          </Section>
        </div>
      )}

      {/* Admin App Documentation */}
      {activeSection === 'admin' && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-600" />
            Admin App Documentation
          </h2>

          <Section title="Access & Roles" icon={Shield} defaultOpen={true}>
            <p className="text-sm text-zinc-600 mb-4">Admin panel is accessible at /admin. Access controlled by email and team membership.</p>
            
            <Role 
              name="Admin (Owner)"
              color="bg-purple-50 border-purple-200"
              permissions={[
                "Full access to all features",
                "View all users, projects, revenue",
                "Manage team members",
                "Process refunds",
                "AI analyze feature requests",
                "Access all tabs"
              ]}
            />
            <Role 
              name="Design Reviewer"
              color="bg-green-50 border-green-200"
              permissions={[
                "View assigned projects only",
                "Update project status",
                "Add notes to projects",
                "Cannot view revenue or users"
              ]}
            />
            <Role 
              name="QA Tester"
              color="bg-blue-50 border-blue-200"
              permissions={[
                "Impersonate customers for testing",
                "Add test credits to accounts",
                "Skip payment for testing",
                "Create and manage bug tickets",
                "Cannot view revenue"
              ]}
            />
          </Section>

          <Section title="Main Navigation Tabs" icon={Layers}>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-zinc-50 rounded-lg">
                <h4 className="font-semibold text-indigo-600">Contacts</h4>
                <p className="text-sm text-zinc-600">Contact form submissions from landing page</p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg">
                <h4 className="font-semibold text-amber-600">Leads</h4>
                <p className="text-sm text-zinc-600">Get Started page submissions</p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg">
                <h4 className="font-semibold text-green-600">Customers</h4>
                <p className="text-sm text-zinc-600">Users, Projects, Themes, Revenue, Features</p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg">
                <h4 className="font-semibold text-purple-600">Team</h4>
                <p className="text-sm text-zinc-600">Team Members, QA Tools</p>
              </div>
            </div>
          </Section>

          <Section title="Contacts Tab" icon={Mail}>
            <Feature 
              title="View Contact Submissions"
              description="All contact form submissions from landing page. Shows: Name, Email, Company, Inquiry Type, Message, Date."
              endpoint="GET /api/utilities/contact-submissions"
            />
            <Feature 
              title="Status Management"
              description="Update status: New → Replied → Archived"
            />
            <Feature 
              title="Sorting"
              description="Sort by: Date, Name, Email, Status (ascending/descending)"
            />
            <Feature 
              title="Stats Dashboard"
              description="Shows: Total Inquiries, New, Replied counts"
            />
          </Section>

          <Section title="Leads Tab" icon={Users}>
            <Feature 
              title="View All Leads"
              description="Leads from Get Started page. Shows: Name, Email, Company, Source, Visit Count, Status, Date."
              endpoint="GET /api/leads"
            />
            <Feature 
              title="Lead Status"
              description="Update status: New → Contacted → Converted → Lost"
            />
            <Feature 
              title="Conversion Tracking"
              description="Stats show conversion rate and funnel metrics"
            />
            <Feature 
              title="Sorting"
              description="Sort by all columns including visit count"
            />
          </Section>

          <Section title="Users Sub-Tab" icon={Users}>
            <Feature 
              title="Registered Users List"
              description="All users who signed up. Shows: Email, Name, Registration Date, Workspace(s), Role."
              endpoint="GET /api/auth/users"
            />
            <Feature 
              title="Workspace Info"
              description="Shows workspace ID, name, and role for each user"
            />
            <Feature 
              title="Sorting"
              description="Sort by: Email, Name, Registration Date, Role"
            />
          </Section>

          <Section title="Projects Sub-Tab" icon={FileText}>
            <Feature 
              title="All Projects"
              description="View all customer projects with full details."
              endpoint="GET /api/projects/admin/all"
            />
            <Feature 
              title="Project Details Panel"
              description="Click project to see: Business info, Pages, Design preview, Payment status, Timeline"
            />
            <Feature 
              title="Status Management"
              description="Update status: draft, generating, ready, revision_requested, in_development, completed, cancelled"
            />
            <Feature 
              title="Assign Reviewer"
              description="Assign project to a design reviewer from the team"
            />
            <Feature 
              title="Generate Payment Link"
              description="Create Razorpay payment link to send to customer"
              endpoint="POST /api/projects/admin/{id}/payment-link"
            />
            <Feature 
              title="Send Payment Link"
              description="Email payment link to customer (requires email integration)"
            />
            <Feature 
              title="Create Project for Customer"
              description="Admin can create projects on behalf of customers by email"
            />
          </Section>

          <Section title="Theme Requests Sub-Tab" icon={Palette}>
            <Feature 
              title="Automatic Request Creation"
              description="When a customer selects a WordPress.org recommended theme and clicks 'Generate Design', an installation request is automatically created. The design generates immediately using WordPress.org theme data."
            />
            <Feature 
              title="View All Requests"
              description="Shows all theme installation requests with: Theme name, slug, screenshot, customer info, project name, status, and date."
              endpoint="GET /api/admin/theme-requests"
            />
            <Feature 
              title="Request Details"
              description="Each request includes: Theme Name, Theme Slug, Screenshot URL, WordPress.org URL, Customer Name &amp; Email, Project Name, Status, Created Date"
            />
            <Feature 
              title="Status Management"
              description="Update status: Pending → Installed (after manually installing on wowcx.io) or Rejected"
            />
            <Feature 
              title="Admin Workflow"
              description="1. Review pending requests 2. Go to WordPress admin (wowcx.io) 3. Install the theme 4. Update status to 'Installed' 5. Theme will appear in library for future customers"
            />
          </Section>

          <Section title="Revenue Sub-Tab" icon={DollarSign}>
            <Feature 
              title="Revenue Dashboard"
              description="Total revenue, monthly breakdown, customer payments"
              endpoint="GET /api/revenue/summary"
            />
            <Feature 
              title="Customer Payments"
              description="View payments by customer with invoice generation"
            />
            <Feature 
              title="Refund Processing"
              description="Process refunds for payments via Razorpay"
              endpoint="POST /api/payments/refund"
            />
          </Section>

          <Section title="Feature Requests Sub-Tab" icon={Sparkles}>
            <Feature 
              title="View All Requests"
              description="Feature requests submitted by customers with vote counts"
              endpoint="GET /api/feature-requests/admin/all"
            />
            <Feature 
              title="AI Prioritization"
              description="Click 'AI Analyze' to have Gemini analyze and score requests by relevance, impact, and effort"
              endpoint="POST /api/feature-requests/admin/analyze"
            />
            <Feature 
              title="Status Management"
              description="Update: Pending, Under Review, Planned, In Progress, Completed, Not Planned"
            />
          </Section>

          <Section title="Team Sub-Tab" icon={UserPlus}>
            <Feature 
              title="Team Members List"
              description="View all team members with roles"
              endpoint="GET /api/projects/admin/team"
            />
            <Feature 
              title="Add Team Member"
              description="Add by email with role: Design Reviewer or QA"
              endpoint="POST /api/projects/admin/team"
            />
            <Feature 
              title="Remove Member"
              description="Remove team member access"
              endpoint="DELETE /api/projects/admin/team/{email}"
            />
            <Feature 
              title="Invite Links"
              description="Generate invite links for: Editor, Viewer, Design Reviewer, QA roles"
            />
          </Section>

          <Section title="QA Sub-Tab" icon={Bug}>
            <Feature 
              title="Customer Impersonation"
              description="Log in as any customer to test their experience"
              endpoint="POST /api/qa/impersonate"
            />
            <Feature 
              title="Add Test Credits"
              description="Add credits to any customer's workspace for testing"
              endpoint="POST /api/qa/add-credits"
            />
            <Feature 
              title="Skip Payment"
              description="Skip payment requirement for a project (testing only)"
              endpoint="POST /api/qa/skip-payment"
            />
            <Feature 
              title="Bug Tickets"
              description="Create and track bug reports found during QA"
              endpoint="GET /api/qa/bugs"
            />
          </Section>

          <Section title="Support Center" icon={MessageSquare}>
            <Feature 
              title="Support Tickets"
              description="Separate page at /admin/support for managing customer support tickets"
              endpoint="/admin/support"
            />
            <Feature 
              title="Ticket Management"
              description="View, respond, and close support tickets"
            />
          </Section>
        </div>
      )}

      {/* API Reference */}
      {activeSection === 'api' && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <Code className="w-6 h-6 text-indigo-600" />
            API Reference
          </h2>

          <Section title="Authentication Endpoints" icon={Shield} defaultOpen={true}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/auth/google/login - Initiate Google OAuth</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/auth/google/callback - OAuth callback handler</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/auth/me - Get current user</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/auth/session - Get session data</div>
              <div className="p-2 bg-red-50 rounded"><span className="text-red-600">POST</span> /api/auth/logout - Logout user</div>
              <div className="p-2 bg-red-50 rounded"><span className="text-red-600">DELETE</span> /api/auth/account - Delete account</div>
            </div>
          </Section>

          <Section title="Project Endpoints" icon={FileText}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/projects - List user&apos;s projects</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/projects/{'{id}'} - Get project details</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/projects - Create project</div>
              <div className="p-2 bg-amber-50 rounded"><span className="text-amber-600">PUT</span> /api/projects/{'{id}'} - Update project</div>
              <div className="p-2 bg-red-50 rounded"><span className="text-red-600">DELETE</span> /api/projects/{'{id}'} - Delete project</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/projects/{'{id}'}/freeze - Freeze design</div>
            </div>
          </Section>

          <Section title="AI Endpoints" icon={Sparkles}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/ai/generate - Generate design (20 credits)</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/ai/revise - Revise design (5 credits)</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/ai/generate-page - Add page (5 credits)</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/ai/analyze-urls - Analyze reference URLs</div>
            </div>
          </Section>

          <Section title="Design Endpoints" icon={Image}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/designs/{'{id}'} - Get design</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/designs/{'{id}'}/share - Generate share link</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/designs/{'{id}'}/export - Export design (2 credits)</div>
              <div className="p-2 bg-red-50 rounded"><span className="text-red-600">DELETE</span> /api/designs/{'{id}'}/share - Revoke share link</div>
            </div>
          </Section>

          <Section title="Theme Endpoints" icon={Palette}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/themes/wordpress - List WordPress themes</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/themes/{'{id}'} - Get theme details</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/themes/request - Request theme installation</div>
            </div>
          </Section>

          <Section title="Payment Endpoints" icon={CreditCard}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/payments/create-order - Create Razorpay order</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/payments/verify - Verify payment</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/payments/history - Payment history</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/payments/refund - Process refund</div>
            </div>
          </Section>

          <Section title="Credits Endpoints" icon={DollarSign}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/credits - Get credit balance</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/credits/check/{'{action}'} - Check if enough credits</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/credits/history/{'{workspace_id}'} - Transaction history</div>
            </div>
          </Section>

          <Section title="Workspace Endpoints" icon={Users}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/workspace - Get current workspace</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/workspaces/members - List members</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/workspaces/invite - Create invite link</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/workspaces/invite/{'{token}'} - Get invite info</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/workspaces/join/{'{token}'} - Join workspace</div>
              <div className="p-2 bg-red-50 rounded"><span className="text-red-600">DELETE</span> /api/workspaces/members/{'{id}'} - Remove member</div>
            </div>
          </Section>

          <Section title="Admin Endpoints" icon={Shield}>
            <div className="space-y-2 font-mono text-sm">
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/projects/admin/all - All projects</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/projects/admin/stats - Project stats</div>
              <div className="p-2 bg-amber-50 rounded"><span className="text-amber-600">PATCH</span> /api/projects/admin/{'{id}'}/status - Update status</div>
              <div className="p-2 bg-amber-50 rounded"><span className="text-amber-600">PATCH</span> /api/projects/admin/{'{id}'}/assign - Assign reviewer</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/projects/admin/team - Get team</div>
              <div className="p-2 bg-blue-50 rounded"><span className="text-blue-600">POST</span> /api/projects/admin/team - Add team member</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/auth/users - All users</div>
              <div className="p-2 bg-green-50 rounded"><span className="text-green-600">GET</span> /api/revenue/summary - Revenue summary</div>
            </div>
          </Section>
        </div>
      )}

      {/* Credits System */}
      {activeSection === 'credits' && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-indigo-600" />
            Credits System Documentation
          </h2>

          <Section title="Credit Costs" icon={DollarSign} defaultOpen={true}>
            <table className="w-full text-sm">
              <thead className="bg-zinc-100">
                <tr>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Credits</th>
                  <th className="text-left p-3">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr><td className="p-3 font-medium">AI Design Generation</td><td className="p-3 text-amber-600 font-bold">20</td><td className="p-3 text-zinc-600">Generate complete multi-page design</td></tr>
                <tr><td className="p-3 font-medium">Design Revision</td><td className="p-3 text-amber-600 font-bold">5</td><td className="p-3 text-zinc-600">Request changes to existing design</td></tr>
                <tr><td className="p-3 font-medium">Add Page</td><td className="p-3 text-amber-600 font-bold">5</td><td className="p-3 text-zinc-600">Generate additional page for design</td></tr>
                <tr><td className="p-3 font-medium">Export Page</td><td className="p-3 text-amber-600 font-bold">2</td><td className="p-3 text-zinc-600">Export as PNG, JPG, HTML, Elementor, or Figma</td></tr>
              </tbody>
            </table>
          </Section>

          <Section title="Credit Packages" icon={CreditCard}>
            <div className="grid grid-cols-3 gap-4">
              <div className="border rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-zinc-900">100</div>
                <div className="text-zinc-500">credits</div>
                <div className="mt-2 text-xl font-semibold text-amber-600">$6 / ₹499</div>
              </div>
              <div className="border-2 border-amber-500 rounded-lg p-4 text-center bg-amber-50">
                <div className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded inline-block mb-2">POPULAR</div>
                <div className="text-3xl font-bold text-zinc-900">300</div>
                <div className="text-zinc-500">credits</div>
                <div className="mt-2 text-xl font-semibold text-amber-600">$16 / ₹1,299</div>
                <div className="text-green-600 text-sm">13% off</div>
              </div>
              <div className="border rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-zinc-900">500</div>
                <div className="text-zinc-500">credits</div>
                <div className="mt-2 text-xl font-semibold text-amber-600">$24 / ₹1,999</div>
                <div className="text-green-600 text-sm">20% off</div>
              </div>
            </div>
          </Section>

          <Section title="Free Credits" icon={Zap}>
            <Feature 
              title="Signup Bonus"
              description="New users receive 50 free credits upon registration - enough to generate their first complete design."
            />
            <Feature 
              title="Credit Sharing"
              description="Credits are shared across all workspace members. The workspace owner's credit balance is used for all team actions."
            />
          </Section>

          <Section title="Credit Flow" icon={Layers}>
            <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-700">
              <li>User initiates action (generate, revise, export)</li>
              <li>System checks workspace credit balance</li>
              <li>If insufficient, shows CreditEnforcementOverlay with purchase options</li>
              <li>If sufficient, deducts credits and performs action</li>
              <li>Transaction logged in credit_transactions collection</li>
              <li>Balance updated in workspace document</li>
            </ol>
          </Section>
        </div>
      )}

      {/* Footer */}
      <div className="bg-zinc-100 rounded-lg p-4 text-center text-sm text-zinc-600">
        <p>AI Design Studio by Wow!CX | Beta Version</p>
        <p className="mt-1">For support, contact: support@wowcx.io</p>
      </div>
    </div>
  );
};

export default DocumentationTab;
